import { create } from 'zustand';
import { saveBundles } from '@/app/actions/plans';
import type { BundleUpdate, BundleUpdateBody } from '@peach/types/bundles/bundle-update.dto.js';
import { BundleType } from '@peach/types/bundles/types.js';
import { PRICE_MIN, priceProblem } from '@peach/types/payments/price-limits.js';
import { getDefaultTitle, hasOwnTitle, newBundle, type BundleDraft } from './bundle-config';
import { dealPrice, referenceCall, referenceRate } from './pricing';
import { callAction } from '@/lib/call-action';

/**
 * The owner's product catalogue while they are editing it, on the storefront
 * itself. A card opens with Edit and commits with Save.
 *
 * ONE WRITE, ALWAYS THE WHOLE CATALOGUE. `POST /bundle` soft-deletes every
 * live bundle and recreates the array it is given, so a product left out of
 * the payload is a product deleted, and ids change on every save. That is why
 * one card is open at a time: a Save sends the others exactly as saved.
 */

/**
 * The last line of defence. A pending edit cannot be saved during unload — the
 * request dies with the page — so the browser asks the owner instead. Mounted
 * only while a save is actually owed, so an idle page never prompts.
 */
const guard = (e: BeforeUnloadEvent) => e.preventDefault();
const holdUnload = (on: boolean) => {
  if (on) window.addEventListener('beforeunload', guard);
  else window.removeEventListener('beforeunload', guard);
};

export function validate(bundles: BundleDraft[]): Record<string, string> {
  const errors: Record<string, string> = {};

  bundles.forEach((bundle) => {
    const problem = priceProblem(bundle.price);
    if (problem) {
      errors[bundle.id] = problem;
    } else if (bundle.type === BundleType.ONE_ON_ONE && bundle.numberOfClasses < 1) {
      errors[bundle.id] = 'A package needs at least one call';
    }
    if (bundle.originalPrice != null && bundle.originalPrice <= bundle.price) {
      errors[`original-${bundle.id}`] = 'A sale price has to sit under the old one';
    }
  });

  return errors;
}

export function buildSavePayload(bundles: BundleDraft[], currency: string): BundleUpdateBody {
  const toUpdate = (bundle: BundleDraft, numberOfClasses: number): BundleUpdate =>
    ({
      numberOfClasses,
      price: bundle.price,
      previousPrice: bundle.originalPrice,
      title: bundle.title,
      description: bundle.description,
      currency,
      type: bundle.type,
      duration: bundle.duration,
    }) as BundleUpdate;

  const trial = bundles.find((b) => b.type === BundleType.TRIAL);

  return {
    bundles: bundles
      .filter((b) => b.type === BundleType.ONE_ON_ONE)
      .map((b) => toUpdate(b, b.numberOfClasses)),
    trialBundle: trial ? toUpdate(trial, 1) : undefined,
  };
}

export interface Terms {
  numberOfClasses?: number;
  duration?: number;
  /** What the owner gets. Nothing but the owner moves it. */
  price?: number;
  /** "Make it a deal": show an old price, struck, above the price. */
  deal?: boolean;
}

interface ProductsStore {
  bundles: BundleDraft[];
  /** Taken off the page this session, still one press from coming back. */
  removed: BundleDraft[];
  errors: Record<string, string>;
  currency: string;
  tutorUsername: string;
  /** The one open card. */
  editingId: string | null;
  /** The catalogue as it stood when Edit was pressed, which Cancel puts back. */
  before: BundleDraft[] | null;
  saving: boolean;
  /** Bumped by every successful save; the storefront refreshes off it. */
  savedAt: number;

  initialize: (currency: string, tutorUsername: string, bundles: BundleDraft[]) => void;
  edit: (bundleId: string) => void;
  cancel: () => void;
  /** Send the catalogue; the open card closes once the server has it. */
  save: () => Promise<void>;
  patch: (bundleId: string, updates: Partial<BundleDraft>) => void;
  /** Change one product's calls, length, price or deal; no other product moves. */
  setTerms: (bundleId: string, terms: Terms) => void;
  add: (type: BundleType) => void;
  remove: (bundleId: string) => void;
  undoRemove: (bundleId: string) => void;
}

export const useBundlesStore = create<ProductsStore>((set, get) => {
  const send = async () => {
    const { bundles, currency, tutorUsername } = get();
    const errors = validate(bundles);
    set({ errors });
    if (Object.keys(errors).length > 0) return;

    set({ saving: true });
    const result = await callAction(() =>
      saveBundles(buildSavePayload(bundles, currency), tutorUsername)
    );
    if (!result.success) {
      set({ saving: false, errors: { general: result.error } });
      return;
    }
    holdUnload(false);
    set({ saving: false, editingId: null, before: null, savedAt: Date.now() });
  };

  /**
   * Saves run one at a time. Each POST replaces the whole catalogue with the
   * snapshot it carries, so two in flight at once are decided by which one the
   * server finishes last: a Remove and its Undo pressed quickly would race.
   */
  let inFlight: Promise<void> = Promise.resolve();
  const commit = () => {
    inFlight = inFlight.then(send, send);
    return inFlight;
  };

  return {
    bundles: [],
    removed: [],
    errors: {},
    currency: 'USD',
    tutorUsername: '',
    editingId: null,
    before: null,
    saving: false,
    savedAt: 0,

    initialize: (currency, tutorUsername, bundles) => {
      set({
        currency,
        tutorUsername,
        bundles,
        removed: [],
        errors: {},
        editingId: null,
        before: null,
      });
    },

    // Nothing opens while a save is in flight: its success closes whatever is open.
    edit: (bundleId) => {
      if (get().editingId || get().saving) return;
      set((state) => ({ editingId: bundleId, before: state.bundles, errors: {} }));
    },

    cancel: () => {
      set((state) => ({
        bundles: state.before ?? state.bundles,
        editingId: null,
        before: null,
        errors: {},
      }));
      holdUnload(false);
    },

    save: commit,

    patch: (bundleId, updates) => {
      set((state) => ({
        bundles: state.bundles.map((b) => (b.id === bundleId ? { ...b, ...updates } : b)),
      }));
      holdUnload(true);
    },

    setTerms: (bundleId, terms) => {
      set((state) => ({
        bundles: state.bundles.map((b) => {
          if (b.id !== bundleId) return b;
          // Off and on again inside one edit gets back the deal as it was saved.
          const saved = state.before?.find((s) => s.id === bundleId);
          const numberOfClasses = terms.numberOfClasses ?? b.numberOfClasses;
          const duration = terms.duration ?? b.duration;
          // The price is the owner's: new calls or minutes never move it.
          const price = Math.max(PRICE_MIN, Math.round(terms.price ?? b.price));
          const deal = terms.deal ?? b.deal;
          return {
            ...b,
            numberOfClasses,
            duration,
            price,
            deal,
            originalPrice: deal ? dealPrice(b.deal ? b : saved, price) : undefined,
            // A product nobody named takes its name from its terms, so it stays true as they change.
            title: hasOwnTitle(b) ? b.title : getDefaultTitle(b.type, numberOfClasses, duration),
          };
        }),
      }));
      holdUnload(true);
    },

    // A new card opens in Edit and is sent by its Save, so
    // Cancel takes it away again. Remove and Undo are single deliberate acts
    // and commit at once; they are offered only while no card is open.
    add: (type) => {
      // The first package is a single call at the intro's price per minute; one
      // added beside it starts as four calls a little under that call's price, as a deal.
      const { bundles, editingId, saving } = get();
      if (editingId || saving) return;
      const calls = referenceCall(bundles) && type === BundleType.ONE_ON_ONE ? 4 : 1;
      const bundle = newBundle(type, calls, referenceRate(bundles));
      set({ bundles: [...bundles, bundle], editingId: bundle.id, before: bundles, errors: {} });
      holdUnload(true);
    },

    remove: (bundleId) => {
      const { bundles, before } = get();
      // The card's unsaved edits go with it; a card never saved has nothing to undo.
      const saved = (before ?? bundles).find((b) => b.id === bundleId);
      set({
        bundles: (before ?? bundles).filter((b) => b.id !== bundleId),
        removed: saved ? [...get().removed, saved] : get().removed,
        editingId: null,
        before: null,
      });
      if (saved) {
        holdUnload(true);
        void commit();
      } else holdUnload(false);
    },

    undoRemove: (bundleId) => {
      const bundle = get().removed.find((b) => b.id === bundleId);
      if (!bundle || get().editingId || get().saving) return;
      set((state) => ({
        bundles: [...state.bundles, bundle],
        removed: state.removed.filter((b) => b.id !== bundleId),
      }));
      holdUnload(true);
      void commit();
    },
  };
});
